import type { Envelope, PageEnvelope } from './types'

type JSONValue = null | boolean | number | string | JSONValue[] | { [key: string]: JSONValue }

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: JSONValue | Record<string, unknown> | unknown[]
  etag?: string
  idempotencyKey?: string
}

export interface APIResponse<T> {
  data: T
  etag: string | null
  lastModified: string | null
  location: string | null
  status: number
}

export class APIError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId: string | null
  readonly details: unknown

  constructor(status: number, code: string, message: string, requestId: string | null, details: unknown) {
    super(message)
    this.name = 'APIError'
    this.status = status
    this.code = code
    this.requestId = requestId
    this.details = details
  }
}

let csrfProvider: () => string | undefined = () => undefined
let unauthorizedHandler: () => void = () => undefined

export function configureAPIClient(options: { csrf: () => string | undefined; unauthorized: () => void }) {
  csrfProvider = options.csrf
  unauthorizedHandler = options.unauthorized
}

export async function apiRequest<T>(target: string, options: RequestOptions = {}): Promise<APIResponse<T>> {
  assertSameOriginTarget(target)
  const method = (options.method ?? 'GET').toUpperCase()
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')
  if (options.etag) headers.set('If-Match', options.etag)
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey)

  const mutation = !['GET', 'HEAD', 'OPTIONS'].includes(method)
  if (mutation) {
    const csrf = csrfProvider()
    if (!csrf)
      throw new APIError(0, 'csrf_unavailable', 'La sesión no tiene un token CSRF válido.', null, null)
    headers.set('X-CSRF-Token', csrf)
  }

  let body: BodyInit | undefined
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.body)
  }

  const response = await fetch(target, {
    ...options,
    method,
    headers,
    body,
    credentials: 'same-origin',
  })
  const payload = await readPayload(response)
  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler()
    const problem = asRecord(payload)
    const code = stringValue(problem?.code) ?? stringValue(problem?.type) ?? `http_${response.status}`
    const message =
      stringValue(problem?.detail) ??
      stringValue(problem?.title) ??
      `La solicitud falló (${response.status}).`
    throw new APIError(
      response.status,
      code,
      message,
      response.headers.get('X-Request-ID'),
      problem?.errors ?? payload,
    )
  }

  return {
    data: payload as T,
    etag: response.headers.get('ETag'),
    lastModified: response.headers.get('Last-Modified'),
    location: response.headers.get('Location'),
    status: response.status,
  }
}

export async function getResource<T>(target: string, signal?: AbortSignal) {
  const response = await apiRequest<Envelope<T>>(target, { signal })
  return { ...response, data: response.data.data }
}

export async function getPage<T>(target: string, signal?: AbortSignal) {
  const response = await apiRequest<PageEnvelope<T>>(target, { signal })
  return { ...response, data: response.data.data, meta: response.data.meta }
}

export function queryString(values: Record<string, string | number | boolean | undefined>) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') params.set(key, String(value))
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

function assertSameOriginTarget(target: string) {
  if (!target.startsWith('/') || target.startsWith('//') || target.includes('\\')) {
    throw new Error('El cliente sólo acepta rutas same-origin.')
  }
  if (!target.startsWith('/api/v1/') && !target.startsWith('/auth/')) {
    throw new Error('Ruta fuera del BFF de BlazeDSP.')
  }
}

async function readPayload(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205 || response.headers.get('Content-Length') === '0') {
    return undefined
  }
  const type = response.headers.get('Content-Type') ?? ''
  if (type.includes('application/json') || type.includes('application/problem+json')) {
    return response.json()
  }
  const text = await response.text()
  return text || undefined
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function stringValue(value: unknown) {
  return typeof value === 'string' && value ? value : undefined
}
